import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import './signature.css';

export default function Signature() {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const sigCanvas = useRef(null);
  const fileInputRef = useRef(null);

  const [signatureType, setSignatureType] = useState('text'); // 'text', 'canvas', or 'upload'
  const [textSignature, setTextSignature] = useState('');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedImagePreview, setUploadedImagePreview] = useState('');
  const [showGuide, setShowGuide] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // New states for preview
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  // Clear canvas when switching to canvas mode
  useEffect(() => {
    if (signatureType === 'canvas' && sigCanvas.current) {
      sigCanvas.current.clear();
    }
  }, [signatureType]);

  // Clear canvas handler
  const clearCanvas = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  // Show guide modal
  const handleUploadClick = () => {
    setShowGuide(true);
  };

  // Close guide and open file picker
  const handleUnderstandGuide = () => {
    setShowGuide(false);
    fileInputRef.current?.click();
  };

  // Handle file selection
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('الرجاء اختيار صورة صحيحة');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('حجم الصورة يجب أن يكون أقل من 5 ميجابايت');
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      // Create FormData
      const formData = new FormData();
      formData.append('signature', file);

      // Send to backend for processing (remove background + crop)
      const response = await fetch(`${API_URL}/api/process-signature`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        // Set processed image (base64)
        setUploadedImage(data.processedImage);
        setUploadedImagePreview(data.processedImage);
      } else {
        setError(data.message || 'فشل معالجة الصورة');
      }
    } catch (err) {
      console.error('Error processing image:', err);
      setError('حدث خطأ أثناء معالجة الصورة');
    } finally {
      setIsProcessing(false);
    }
  };

  // Clear uploaded image
  const clearUpload = () => {
    setUploadedImage(null);
    setUploadedImagePreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle signature completion - Generate preview
  const handleSubmit = async () => {
    setError('');

    // Validate signature
    if (signatureType === 'text' && !textSignature.trim()) {
      setError('الرجاء إدخال الاسم للتوقيع');
      return;
    }

    if (signatureType === 'canvas' && sigCanvas.current.isEmpty()) {
      setError('الرجاء رسم التوقيع');
      return;
    }

    if (signatureType === 'upload' && !uploadedImage) {
      setError('الرجاء رفع صورة التوقيع');
      return;
    }

    setIsLoadingPreview(true);

    try {
      // Get signature data based on type
      let signatureData;
      if (signatureType === 'text') {
        signatureData = textSignature.trim();
      } else if (signatureType === 'canvas') {
        signatureData = sigCanvas.current.toDataURL('image/png');
      } else {
        signatureData = uploadedImage;
      }

      // Generate preview with signature
      const response = await fetch(
        `${API_URL}/api/contract/${contractId}/preview`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            signatureData,
            signatureType,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('فشل في إنشاء المعاينة');
      }

      // Get PDF blob
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      setPreviewUrl(url);
      setShowPreview(true);
    } catch (err) {
      console.error('Error generating preview:', err);
      setError('حدث خطأ أثناء إنشاء المعاينة. الرجاء المحاولة مرة أخرى.');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  // Handle final confirmation - Save contract
  const handleConfirmation = async () => {
    setIsConfirming(true);

    try {
      // Get signature data
      let signatureData;
      if (signatureType === 'text') {
        signatureData = textSignature.trim();
      } else if (signatureType === 'canvas') {
        signatureData = sigCanvas.current.toDataURL('image/png');
      } else {
        signatureData = uploadedImage;
      }

      // Save signature to database
      const response = await fetch(
        `${API_URL}/api/contract/${contractId}/signature`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            signatureData,
            signatureType,
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        // Show success modal
        setShowSuccessModal(true);

        // Redirect to home after 2 seconds
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        setError(data.message || 'حدث خطأ أثناء حفظ العقد');
        setShowPreview(false);
      }
    } catch (err) {
      console.error('Error confirming contract:', err);
      setError('حدث خطأ أثناء حفظ العقد. الرجاء المحاولة مرة أخرى.');
      setShowPreview(false);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div className="signature-container">
      {/* Left Side - Video or Preview */}
      <div className="video-section">
        {!showPreview ? (
          <video autoPlay muted loop className="signature-video">
            <source src="/assets/images/ithra_video.mp4" type="video/mp4" />
            المتصفح لا يدعم عرض الفيديو
          </video>
        ) : (
          <div className="preview-section">
            <h3 className="preview-title">معاينة العقد</h3>
            {previewUrl && (
              <iframe
                src={previewUrl}
                className="contract-preview-iframe"
                title="Contract Preview"
              />
            )}
          </div>
        )}
      </div>

      {/* Right Side - Signature Interface */}
      <div className="signature-section">
        <div className="signature-content">
          <h2 className="signature-title">توقيع العقد</h2>
          <p className="signature-subtitle">الرجاء التوقيع لإتمام العقد</p>

          {/* Signature Type Selection */}
          <div className="signature-type-selector">
            <label className="radio-option">
              <input
                type="radio"
                value="text"
                checked={signatureType === 'text'}
                onChange={(e) => setSignatureType(e.target.value)}
              />
              <span>نص</span>
            </label>

            <label className="radio-option">
              <input
                type="radio"
                value="canvas"
                checked={signatureType === 'canvas'}
                onChange={(e) => setSignatureType(e.target.value)}
              />
              <span>رسم</span>
            </label>

            <label className="radio-option">
              <input
                type="radio"
                value="upload"
                checked={signatureType === 'upload'}
                onChange={(e) => setSignatureType(e.target.value)}
              />
              <span>رفع صورة</span>
            </label>
          </div>

          {/* Text Signature Input */}
          {signatureType === 'text' && (
            <div className="text-signature-section">
              <input
                type="text"
                className="text-signature-input"
                placeholder="اكتب اسمك هنا"
                value={textSignature}
                onChange={(e) => setTextSignature(e.target.value)}
                maxLength={50}
              />
            </div>
          )}

          {/* Canvas Signature Drawing */}
          {signatureType === 'canvas' && (
            <div className="canvas-signature-section">
              <div className="canvas-container">
                <SignatureCanvas
                  ref={sigCanvas}
                  penColor="#000000ff"
                  canvasProps={{
                    className: 'signature-canvas',
                  }}
                />
              </div>
              <button
                type="button"
                className="clear-button"
                onClick={clearCanvas}
              >
                مسح
              </button>
            </div>
          )}

          {/* Upload Signature Section */}
          {signatureType === 'upload' && (
            <div className="upload-signature-section">
              {!uploadedImagePreview ? (
                <>
                  <button
                    type="button"
                    className="upload-button"
                    onClick={handleUploadClick}
                    disabled={isProcessing}
                  >
                    {isProcessing ? 'جاري المعالجة...' : 'رفع صورة التوقيع'}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                </>
              ) : (
                <div className="uploaded-preview">
                  <img src={uploadedImagePreview} alt="Signature Preview" />
                  <button
                    type="button"
                    className="clear-button"
                    onClick={clearUpload}
                  >
                    حذف
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Error Message */}
          {error && <div className="error-message">{error}</div>}

          {/* Submit/Confirmation Buttons */}
          {!showPreview ? (
            <button
              className="submit-signature-button"
              onClick={handleSubmit}
              disabled={isLoadingPreview}
            >
              {isLoadingPreview ? 'جاري إنشاء المعاينة...' : 'معاينة العقد'}
            </button>
          ) : (
            <div className="preview-actions">
              <button
                className="retry-preview-button"
                onClick={() => {
                  setShowPreview(false);
                  setPreviewUrl('');
                }}
                disabled={isConfirming}
              >
                إعادة المعاينة
              </button>
              <button
                className="confirm-signature-button"
                onClick={handleConfirmation}
                disabled={isConfirming}
              >
                {isConfirming ? 'جاري الحفظ...' : 'تأكيد وحفظ العقد'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Guide Modal */}
      {showGuide && (
        <div
          className="guide-modal-overlay"
          onClick={() => setShowGuide(false)}
        >
          <div className="guide-modal" onClick={(e) => e.stopPropagation()}>
            <h3>إرشادات رفع التوقيع</h3>
            <img
              src="/assets/images/guide.png"
              alt="Guide"
              className="guide-image"
            />
            <button
              className="understand-button"
              onClick={handleUnderstandGuide}
            >
              فهمت
            </button>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="success-modal-overlay">
          <div className="success-modal">
            <div className="success-icon">✓</div>
            <h3>تم حفظ العقد بنجاح!</h3>
            <p>جاري التحويل إلى الصفحة الرئيسية...</p>
          </div>
        </div>
      )}
    </div>
  );
}
